<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import type { SchedulerBrandType } from '@/constants/scheduler'

import omnisendFlow1 from '@/assets/pics/omnisend-flow-1.png'
import omnisendFlow2 from '@/assets/pics/omnisend-flow-2.png'
import omnisendFlow3 from '@/assets/pics/omnisend-flow-3.png'
import { Icon } from '@/components/icon'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useAmplitude } from '@/composables/use-amplitude'
import { SCHEDULER_BOOKING_URLS } from '@/constants/scheduler'
import OnboardingRetentionLayout from '@/pages/onboarding-retention/components/OnboardingRetentionLayout.vue'
import { recaptureOnboardingAuditApi } from '@/services/api/onboarding-audit.api'
import { onboardingProgressCompleteApi } from '@/services/api/platform.api'
import { recaptureFreeApi } from '@/services/api/shopify.api'
import { useOnboardingRetentionProgressStore } from '@/stores/onboarding-retention-progress'
import { useUserStore } from '@/stores/user'

definePage({
  path: '/onboarding-retention/step4',
  meta: { layout: false },
})

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const { track } = useAmplitude()
const STEP4_BACK_TARGET_KEY = 'retention-onboarding-step4-back-target'
const STEP4_ENTRY_MODE_KEY = 'retention-onboarding-step4-entry-mode'

function setStep4EntryState(mode: string, backTarget: string) {
  if (typeof window === 'undefined')
    return
  window.sessionStorage.setItem(STEP4_ENTRY_MODE_KEY, mode)
  window.sessionStorage.setItem(STEP4_BACK_TARGET_KEY, backTarget)
}

function getStep4EntryMode() {
  if (typeof window === 'undefined')
    return ''
  return window.sessionStorage.getItem(STEP4_ENTRY_MODE_KEY) || ''
}

function getStep4BackTarget() {
  if (typeof window === 'undefined')
    return ''
  return window.sessionStorage.getItem(STEP4_BACK_TARGET_KEY) || ''
}

const progressStore = useOnboardingRetentionProgressStore()
const { progress, loaded, resolvedEsp, selectedEspMeta } = storeToRefs(progressStore)
const userStore = useUserStore()
const i18nParams = computed(() => ({ esp: selectedEspMeta.value?.displayName ?? 'Klaviyo' }))

const ESP_SUPPORT_DOC_URLS: Record<string, string> = {
  klaviyo: 'https://support.attribuly.com/en/articles/10001282-getting-started-with-klaviyo',
  omnisend: 'https://support.attribuly.com/en/articles/12340792-getting-started-with-omnisend',
  cartsee: 'https://support.attribuly.com/en/articles/10001282-getting-started-with-klaviyo',
  manba: 'https://support.attribuly.com/en/articles/10001282-getting-started-with-klaviyo',
}
const espSupportDocUrl = computed(() => ESP_SUPPORT_DOC_URLS[resolvedEsp.value ?? 'klaviyo'] ?? ESP_SUPPORT_DOC_URLS.klaviyo)

const completing = ref(false)
const cloneDemoInteracted = ref(false)
const preventDemoInteracted = ref(false)
const confirmedOverride = ref<boolean | null>(null)

const RETENTION_AUDIT_CACHE_KEY = 'retention_onboarding_audit_cache_v1'
const ESTIMATED_REVENUE_THRESHOLD_USD = 500
const auditWindowDays = 30

const estimatedMonthlyIncrementalRevenueUsd = ref<number | null>(null)
const auditRefreshedAt = ref<string | null>(null)
const loadingEstimate = ref(false)
const schedulerInteracted = ref(false)
const schedulerInteractionAt = ref<number | null>(null)
const schedulerLoadAfterInteractionCount = ref(0)
const schedulerIframeRef = ref<HTMLIFrameElement | null>(null)
const scheduleEventTracked = ref(false)

// Zoom Scheduler embed 的响应式宽度断点(实测):
// ≤560px 移动端堆叠布局、~600-880px 两栏布局会横向裁切、≥900px 三栏布局完整。
// 尺寸交给下方 .scheduler-embed 容器查询控制,避开裁切区。

const forceUnconfirmed = computed(() => route.query?.retest === '1' || route.query?.reset === '1')
const progressData = computed<Record<string, any>>(() => progress.value?.progress_data || {})
const isConfirmed = computed(() => (forceUnconfirmed.value ? false : (confirmedOverride.value ?? Boolean(progress.value?.is_completed))))
const schedulerOptions = computed<Array<{
  key: SchedulerBrandType
  eyebrow: string
  title: string
  description: string
  icon: string
  href: string
}>>(() => [
  {
    key: 'china',
    eyebrow: t('onboardingRetention.step4.schedulerRegion.china.eyebrow'),
    title: t('onboardingRetention.step4.schedulerRegion.china.title'),
    description: t('onboardingRetention.step4.schedulerRegion.china.description'),
    icon: 'lucide:map-pin',
    href: SCHEDULER_BOOKING_URLS.china,
  },
  {
    key: 'global',
    eyebrow: t('onboardingRetention.step4.schedulerRegion.global.eyebrow'),
    title: t('onboardingRetention.step4.schedulerRegion.global.title'),
    description: t('onboardingRetention.step4.schedulerRegion.global.description'),
    icon: 'lucide:globe-2',
    href: SCHEDULER_BOOKING_URLS.global,
  },
])
const step4Entry = computed(() => String(route.query?.entry || ''))
const step4SourceStep = computed<number | null>(() => {
  const sourceStepRaw = String(route.query?.sourceStep || '')
  const sourceStep = Number(sourceStepRaw)
  if ([1, 2, 3, 4].includes(sourceStep))
    return sourceStep
  const prev = String(route.query?.prev || '')
  const match = prev.match(/\/onboarding-retention\/step(\d+)/)
  if (!match)
    return null
  const step = Number(match[1])
  return [1, 2, 3, 4].includes(step) ? step : null
})
const isBookCallStep1Flow = computed(() => (
  (step4Entry.value === 'book-call' && step4SourceStep.value === 1)
  || getStep4EntryMode() === 'book-call-step1'
))

const completedSteps = computed(() => {
  const out: number[] = []
  if (progressData.value.step1Connected === true)
    out.push(1)
  if (progressData.value.step2ConversionFeedConfigured === true)
    out.push(2)
  if (progressData.value.step3AppEmbedEnabled === true)
    out.push(3)
  if (isConfirmed.value)
    out.push(4)
  return out
})
const clickableSteps = computed(() => {
  if (isBookCallStep1Flow.value)
    return [1, 4]
  const out = new Set<number>([...completedSteps.value, 4])
  return [...out].sort((a, b) => a - b)
})
const isHighValueEstimate = computed(() => {
  const esp = resolvedEsp.value ?? 'klaviyo'
  if (esp === 'cartsee' || esp === 'manba')
    return true
  return estimatedMonthlyIncrementalRevenueUsd.value !== null
    && estimatedMonthlyIncrementalRevenueUsd.value >= ESTIMATED_REVENUE_THRESHOLD_USD
})
const shouldShowLowValueCta = computed(() => !isBookCallStep1Flow.value && !isHighValueEstimate.value)
const shouldShowSchedulerCard = computed(() => isBookCallStep1Flow.value || isHighValueEstimate.value)

async function ensureLoaded() {
  if (loaded.value)
    return
  let result: any = null
  try {
    result = await progressStore.loadFromBackend()
  }
  catch (e) {
    console.warn('[retention-onboarding step4] loadFromBackend failed, fallback local:', e)
  }
  if (!resolvedEsp.value) {
    if (result !== null) {
      router.replace('/onboarding-retention/step0')
      return
    }
    progressStore.setEsp('klaviyo')
  }
}

async function ensureCurrentStepSaved() {
  if (isConfirmed.value)
    return
  if (progress.value?.current_step === 4)
    return
  try {
    await progressStore.save({
      current_step: 4,
      current_platform: 'retention',
      progress_data: {
        ...(progress.value?.progress_data || {}),
      },
    })
    if (progress.value) {
      progress.value = {
        ...progress.value,
        current_step: 4,
        current_platform: 'retention',
      }
    }
  }
  catch (e) {
    console.warn('[retention-onboarding step4] save current_step=4 failed (no token / offline):', e)
  }
}

async function completeStep4() {
  if (completing.value || isConfirmed.value)
    return
  if (forceUnconfirmed.value)
    return
  completing.value = true
  try {
    await progressStore.complete()
    await onboardingProgressCompleteApi({ version: 'v1' })
    // 清除 benchmark onboarding 全套标记：retention onboarding 完成后，后续会话不再特殊处理
    try {
      localStorage.removeItem('benchmark_onboarding_flag')
      localStorage.removeItem('benchmark_onboarding_pending')
    } catch (_e) {}
    if (progress.value) {
      progress.value = {
        ...progress.value,
        is_completed: true,
      }
    }
    confirmedOverride.value = true
  }
  finally {
    completing.value = false
  }
}

function loadAuditCache() {
  try {
    const raw = window.sessionStorage.getItem(RETENTION_AUDIT_CACHE_KEY)
    if (!raw)
      return
    const parsed = JSON.parse(raw) as any
    const revenue = Number(parsed?.estimatedMonthlyIncrementalRevenueUsd)
    estimatedMonthlyIncrementalRevenueUsd.value = Number.isFinite(revenue) ? revenue : null
    auditRefreshedAt.value = typeof parsed?.refreshedAt === 'string' ? parsed.refreshedAt : null
  }
  catch {
  }
}

async function ensureEstimatedRevenueReady() {
  if (estimatedMonthlyIncrementalRevenueUsd.value !== null)
    return

  loadingEstimate.value = true
  loadAuditCache()
  if (estimatedMonthlyIncrementalRevenueUsd.value !== null) {
    loadingEstimate.value = false
    return
  }

  try {
    const result = await recaptureOnboardingAuditApi({
      window_days: auditWindowDays,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    })
    if (result.status !== 'ready')
      return

    const revenue = result.estimatedMonthlyIncrementalRevenueUsd
    estimatedMonthlyIncrementalRevenueUsd.value = typeof revenue === 'number' ? revenue : null
    auditRefreshedAt.value = result.refreshedAt ?? null

    try {
      window.sessionStorage.setItem(RETENTION_AUDIT_CACHE_KEY, JSON.stringify({
        refreshedAt: result.refreshedAt ?? null,
        windowDays: result.windowDays ?? auditWindowDays,
        abandonmentFlowRevenue30dUsd: result.abandonmentFlowRevenue30dUsd ?? null,
        upliftCoefficient: result.upliftCoefficient ?? null,
        estimatedMonthlyIncrementalRevenueUsd: result.estimatedMonthlyIncrementalRevenueUsd ?? null,
      }))
    }
    catch {
    }
  }
  catch {
  }
  finally {
    loadingEstimate.value = false
  }
}

async function handleSetupDone() {
  if (completing.value)
    return
  const action = 'setup_done'
  track('retention_onboarding_step4_completion_action_click', { action })
  if (shouldShowLowValueCta.value) {
    try {
      await recaptureFreeApi()
      sessionStorage.removeItem('attribuly_init_app_cache')
      localStorage.removeItem('retention_onboarding_lock')
      // 清除 benchmark onboarding 全套标记：低价值 free 激活后后续会话不再特殊处理
      try {
        localStorage.removeItem('benchmark_onboarding_flag')
        localStorage.removeItem('benchmark_onboarding_pending')
      } catch (_e) {}
      ;(userStore.isAppInitialized as any) = false
      await progressStore.complete()
      await onboardingProgressCompleteApi({ version: 'v1' })
      if (progress.value) {
        progress.value = {
          ...progress.value,
          is_completed: true,
        }
      }
      confirmedOverride.value = true
    }
    catch (error) {
      console.error('Failed to call recapture-free API:', error)
    }
  }
  if (shouldShowSchedulerCard.value) {
    await completeStep4()
  }

  track('admission_path_assigned', {
    admission_path: 'self_serve_free',
    source: 'retention_onboarding_step4',
    action,
    from: 'setup_completed',
    estimated_monthly_incremental_revenue_usd: estimatedMonthlyIncrementalRevenueUsd.value,
    refreshed_at: auditRefreshedAt.value,
  })
  track('free_activated', {
    source: 'retention_onboarding_step4',
    action,
  })
  router.push('/home')
}

function back() {
  track('retention_onboarding_step4_back_click')
  if (isBookCallStep1Flow.value) {
    router.push('/onboarding-retention/step1')
    return
  }
  const prev = (route.query?.prev as string | undefined) || getStep4BackTarget()
  if (prev)
    router.push(prev)
  else router.push('/onboarding-retention/step3')
}

function handleViewSetupDocClick() {
  track('retention_onboarding_step4_view_setup_doc_click', { esp: resolvedEsp.value })
}

function handleCloneDemoPointerDown() {
  if (cloneDemoInteracted.value)
    return
  cloneDemoInteracted.value = true
  track('retention_onboarding_step4_clone_your_abandoned_flow_get_started_click')
}

function handlePreventDemoPointerDown() {
  if (preventDemoInteracted.value)
    return
  preventDemoInteracted.value = true
  track('retention_onboarding_step4_prevent_duplicate_emails_get_started_click')
}

function handleSchedulerExternalClick(type: SchedulerBrandType) {
  track('retention_onboarding_step4_scheduler_external_click', {
    brand_type: type,
  })
  track('retention_onboarding_step4_schedule_event_click')
  void completeStep4()
}
onMounted(async () => {
  track('retention_onboarding_step4_pageview', { esp: resolvedEsp.value })
  // if (typeof window !== 'undefined')
  //  window.addEventListener('message', handleSchedulerMessage)
  await ensureLoaded()
  if (resolvedEsp.value !== 'cartsee' && resolvedEsp.value !== 'manba') {
    loadAuditCache()
  }
  if (step4Entry.value === 'book-call' && step4SourceStep.value === 1) {
    setStep4EntryState('book-call-step1', '/onboarding-retention/step1')
  }
  else if (step4SourceStep.value === 3) {
    setStep4EntryState('normal-step3', '/onboarding-retention/step3')
  }
  if (!isBookCallStep1Flow.value && resolvedEsp.value !== 'cartsee' && resolvedEsp.value !== 'manba')
    await ensureEstimatedRevenueReady()
  if (forceUnconfirmed.value) {
    confirmedOverride.value = false
    if (progress.value) {
      progress.value = {
        ...progress.value,
        is_completed: false,
      }
    }
  }
  await ensureCurrentStepSaved()

  await nextTick()
  if (route.hash === '#scheduler') {
    requestAnimationFrame(() => {
      document.getElementById('onboarding-retention-step4-scheduler')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }
})
</script>

<template>
  <OnboardingRetentionLayout
    :current-step="4"
    :completed-steps="completedSteps"
    :clickable-steps="clickableSteps"
    title-key="onboardingRetention.step4.title"
    subtitle-key="onboardingRetention.step4.subtitle"
  >
    <div class="space-y-4">
      <Card class="rounded-[var(--p-border-radius-300)] border border-black bg-white shadow-none overflow-hidden">
        <CardHeader class="pb-0">
          <div class="flex items-center justify-between gap-3">
            <CardTitle class="text-base">
              {{ t('onboardingRetention.step4.demoTitle', i18nParams) }}
            </CardTitle>
            <a
              v-if="resolvedEsp !== 'cartsee' && resolvedEsp !== 'manba'"
              class="flex items-center gap-2 text-sm text-[#28704C] hover:underline underline-offset-2"
              :href="espSupportDocUrl"
              target="_blank"
              rel="noopener noreferrer"
              @click="handleViewSetupDocClick"
            >
              <Icon name="lucide:file-text" class="h-4 w-4" />
              <span>{{ t('onboardingRetention.step4.viewSetupDoc') }}</span>
            </a>
          </div>
        </CardHeader>
        <CardContent class="pt-0">
          <template v-if="resolvedEsp === 'cartsee' || resolvedEsp === 'manba'">
            <div class="mt-4 space-y-3 text-sm leading-relaxed text-[#1F2937]">
              <p>{{ t(`onboardingRetention.step4.${resolvedEsp}.paragraph1`) }}</p>
              <p>{{ t(`onboardingRetention.step4.${resolvedEsp}.paragraph2`) }}</p>
              <p class="pt-2 text-[#6D6D6D]">
                {{ t(`onboardingRetention.step4.${resolvedEsp}.paragraph3`) }}
              </p>
            </div>
          </template>
          <template v-else>
            <div class="mt-1 text-sm leading-snug">
              <div class="text-slate-950 font-semibold">
                {{ t('onboardingRetention.step4.cloneFlowTitle') }}
              </div>
              <div class="mt-1 text-[#6D6D6D]">
                {{ t('onboardingRetention.step4.cloneFlowDesc') }}
              </div>
            </div>

            <template v-if="resolvedEsp === 'omnisend'">
              <div class="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div class="overflow-hidden rounded-[var(--p-border-radius-300)] border border-black/10 bg-white">
                  <img
                    :src="omnisendFlow1"
                    :alt="t('onboardingRetention.step4.demoIframeTitle')"
                    class="h-auto w-full object-contain"
                  >
                </div>
                <div class="overflow-hidden rounded-[var(--p-border-radius-300)] border border-black/10 bg-white">
                  <img
                    :src="omnisendFlow2"
                    :alt="t('onboardingRetention.step4.demoIframeTitle')"
                    class="h-auto w-full object-contain"
                  >
                </div>
              </div>
            </template>
            <template v-else>
              <div class="mt-2 overflow-hidden rounded-[var(--p-border-radius-300)]" style="position: relative; padding-bottom: calc(52.38095238095239% + 41px); height: 0; width: 100%;" @pointerdown="handleCloneDemoPointerDown">
                <iframe
                  src="https://demo.arcade.software/JfczOvTZncw9bqb87Owc?embed&embed_mobile=inline&embed_desktop=inline&show_copy_link=true"
                  :title="t('onboardingRetention.step4.demoIframeTitle')"
                  frameborder="0"
                  loading="lazy"
                  allowfullscreen
                  allow="clipboard-write"
                  style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; color-scheme: light;"
                />
              </div>
            </template>

            <div class="mt-8 text-sm leading-snug">
              <div class="text-slate-950 font-semibold">
                {{ t('onboardingRetention.step4.doubleSendingTitle', i18nParams) }}
              </div>
              <div class="mt-1 text-[#6D6D6D]">
                {{ t('onboardingRetention.step4.doubleSendingDesc', i18nParams) }}
              </div>
            </div>

            <template v-if="resolvedEsp === 'omnisend'">
              <div class="mt-4 space-y-3 text-[#6D6D6D] text-sm leading-relaxed">
                <p>{{ t('onboardingRetention.step4.omnisend.duplicateSkipInstruction') }}</p>
                <p>{{ t('onboardingRetention.step4.omnisend.duplicateBestPractice') }}</p>
              </div>
              <div class="mt-6 overflow-hidden rounded-[var(--p-border-radius-300)] border border-black/10 bg-white">
                <img
                  :src="omnisendFlow3"
                  :alt="t('onboardingRetention.step4.doubleSendingIframeTitle')"
                  class="h-auto w-full object-contain"
                >
              </div>
            </template>
            <template v-else>
              <div class="mt-6 overflow-hidden rounded-[var(--p-border-radius-300)]" style="position: relative; padding-bottom: calc(50.3401% + 41px); height: 0px; width: 100%;" @pointerdown="handlePreventDemoPointerDown">
                <iframe
                  src="https://demo.arcade.software/sAxb5ylll0uFGnGZrjMP?embed&embed_mobile=inline&embed_desktop=inline&show_copy_link=true"
                  :title="t('onboardingRetention.step4.doubleSendingIframeTitle')"
                  frameborder="0"
                  loading="lazy"
                  webkitallowfullscreen
                  mozallowfullscreen
                  allowfullscreen
                  allow="clipboard-write"
                  style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; color-scheme: light;"
                />
              </div>
            </template>
          </template>
        </CardContent>
      </Card>

      <Card v-if="shouldShowSchedulerCard" id="onboarding-retention-step4-scheduler" class="rounded-[var(--p-border-radius-300)] border border-black bg-white shadow-none overflow-hidden">
        <CardHeader class="pb-0">
          <CardTitle class="text-base">
            {{ t('onboardingRetention.step4.schedulerTitle') }}
          </CardTitle>
        </CardHeader>
        <CardContent class="pt-0">
          <div class="mb-3 text-sm text-[#6D6D6D] leading-snug">
            <ul class="mt-0.5 space-y-1">
              <li class="flex items-start gap-2">
                <Icon name="lucide:badge-check" class="h-4 w-4 mt-0.5 text-[#6D6D6D]" />
                <span>{{ t('onboardingRetention.step4.schedulerIntro.line1') }}</span>
              </li>
            </ul>
          </div>
          <div class="w-full scheduler-embed">
            <div class="scheduler-panel">
              <div class="scheduler-panel-head">
                <div class="min-w-0">
                  <div class="scheduler-kicker">
                    <Icon name="lucide:calendar-check" class="h-4 w-4" />
                    <span>
                      {{ t('onboardingRetention.step4.schedulerRegion.kicker') }}
                    </span>
                  </div>
                  <h3 class="scheduler-title">
                    {{ t('onboardingRetention.step4.schedulerRegion.step1Title') }}
                  </h3>
                </div>

                <div class="scheduler-link-pill">
                  <Icon name="lucide:video" class="h-3.5 w-3.5" />
                  <span>Zoom</span>
                </div>
              </div>

              <div class="scheduler-choice-grid">
                <a
                  v-for="option in schedulerOptions"
                  :key="option.key"
                  :href="option.href"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="scheduler-choice-card group"
                  :aria-label="option.title"
                  @click="handleSchedulerExternalClick(option.key)"
                >
                  <span class="scheduler-choice-icon">
                    <Icon :name="option.icon" class="h-5 w-5" />
                  </span>
                  <span class="min-w-0">
                    <span class="scheduler-choice-eyebrow">
                      {{ option.eyebrow }}
                    </span>
                    <span class="scheduler-choice-title">
                      {{ option.title }}
                    </span>
                    <span class="scheduler-choice-description">
                      {{ option.description }}
                    </span>
                  </span>
                  <span class="scheduler-choice-action" aria-hidden="true">
                    <Icon name="lucide:arrow-up-right" class="h-4 w-4" />
                  </span>
                </a>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div class="flex items-center justify-between gap-3 pt-2">
        <div class="flex items-center gap-3">
          <Button variant="outline" class="bg-white border-black text-black shadow-none hover:bg-white" @click="back">
            {{ t('onboardingRetention.actions.back') }}
          </Button>
        </div>

        <Button
          v-if="shouldShowLowValueCta"
          class="h-11 bg-[#111827] text-white shadow-none hover:bg-[#111827]/90"
          :disabled="completing"
          @click="handleSetupDone"
        >
          {{ t('onboardingRetention.step4.completion.actions.setupDone') }}
        </Button>
      </div>
    </div>

    <template #right>
      <div class="h-full w-full bg-[#F5F4ED]">
        <div class="px-8 pt-8 pb-8">
          <div class="text-[10px] font-semibold tracking-[0.18em] text-[#6D6D6D]">
            {{ t('onboardingRetention.step4.valueCard.kicker') }}
          </div>
          <div class="mt-2 text-[20px] leading-tight font-semibold text-[#111827]">
            {{ t('onboardingRetention.step4.valueCard.title') }}
          </div>

          <div class="mt-6 rounded-[var(--p-border-radius-300)] bg-[#F2D8D8] px-6 py-4">
            <div class="flex items-center gap-2 text-[11px] font-semibold tracking-[0.18em] text-[#8F2C2C]">
              <Icon name="lucide:x" class="h-4 w-4" />
              <span class="uppercase">{{ t('onboardingRetention.step4.valueCard.withoutTitle') }}</span>
            </div>

            <div class="mt-3 space-y-3 text-[14px] leading-snug text-[#8F2C2C]">
              <div class="flex items-center gap-6">
                <div class="w-[140px] flex items-center">
                  <span class="whitespace-pre-line leading-tight relative -top-0.5">{{ t('onboardingRetention.step4.valueCard.shoppersLabel') }}</span>
                </div>
                <div class="w-[280px]">
                  <div class="h-9 w-full rounded-[var(--p-border-radius-200)] bg-[rgba(255,209,209,0.65)] px-4 flex items-center text-[#8F2C2C]">
                    2,028
                  </div>
                  <div class="mt-2 flex items-center gap-2 text-[13px] text-[#8F2C2C]">
                    <Icon name="lucide:arrow-down" class="h-4 w-4" />
                    <span class="whitespace-nowrap">{{ t('onboardingRetention.step4.valueCard.withoutNote') }}</span>
                  </div>
                </div>
              </div>

              <div class="flex items-center gap-6">
                <div class="w-[140px] flex items-center">
                  {{ t('onboardingRetention.step4.valueCard.enterFlowLabel') }}
                </div>
                <div class="w-[280px]">
                  <div class="h-9 w-full rounded-[var(--p-border-radius-200)] bg-[rgba(238,242,247,0.7)] px-4 flex items-center text-[#8F2C2C]">
                    0
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="py-4 flex justify-center text-[#C7C7C7]">
            <Icon name="lucide:arrow-down" class="h-7 w-7" />
          </div>

          <div class="rounded-[var(--p-border-radius-300)] bg-[#DCEAD9] px-6 py-4">
            <div class="flex items-center gap-2 text-[11px] font-semibold tracking-[0.18em] text-[#1F5B3A]">
              <Icon name="lucide:check" class="h-4 w-4" />
              <span class="uppercase">{{ t('onboardingRetention.step4.valueCard.withTitle') }}</span>
            </div>

            <div class="mt-3 space-y-3 text-[14px] leading-snug text-[#1F5B3A]">
              <div class="flex items-center gap-6">
                <div class="w-[140px] flex items-center">
                  <span class="whitespace-pre-line leading-tight relative -top-0.5">{{ t('onboardingRetention.step4.valueCard.shoppersLabel') }}</span>
                </div>
                <div class="w-[280px]">
                  <div class="h-9 w-full rounded-[var(--p-border-radius-200)] bg-[rgba(134,239,172,0.6)] px-4 flex items-center text-[#1F5B3A]">
                    2,028
                  </div>
                  <div class="mt-2 flex items-center gap-2 text-[13px] text-[#1F5B3A]">
                    <Icon name="lucide:arrow-down" class="h-4 w-4" />
                    <span class="whitespace-nowrap">{{ t('onboardingRetention.step4.valueCard.withNote') }}</span>
                  </div>
                </div>
              </div>

              <div class="flex items-center gap-6">
                <div class="w-[140px] flex items-center">
                  {{ t('onboardingRetention.step4.valueCard.enterFlowLabel') }}
                </div>
                <div class="w-[280px]">
                  <div class="h-9 w-full rounded-[var(--p-border-radius-200)] bg-[rgba(40,107,73,0.85)] px-4 flex items-center text-white whitespace-nowrap">
                    2,028 (100%)
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </OnboardingRetentionLayout>
</template>

<style scoped>
.scheduler-embed {
  width: 100%;
}

.scheduler-panel {
  border: 1px solid rgba(20, 20, 19, 0.12);
  border-radius: 14px;
  background: #FCFCF8;
  padding: 16px;
  overflow: hidden;
}

.scheduler-panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.scheduler-kicker {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #28704C;
  font-size: 13px;
  font-weight: 650;
}

.scheduler-title {
  margin-top: 6px;
  max-width: 560px;
  color: #111827;
  font-size: 19px;
  font-weight: 650;
  line-height: 1.25;
}

.scheduler-link-pill {
  display: inline-flex;
  min-height: 34px;
  flex-shrink: 0;
  align-items: center;
  gap: 6px;
  border: 1px solid rgba(20, 20, 19, 0.12);
  border-radius: 999px;
  background: #FFFFFC;
  padding: 0 11px;
  color: rgba(17, 24, 39, 0.72);
  font-size: 12px;
  font-weight: 650;
}

.scheduler-choice-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 16px;
}

.scheduler-choice-card {
  position: relative;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) 34px;
  gap: 14px;
  width: 100%;
  min-height: 122px;
  align-items: center;
  border: 1px solid rgba(20, 20, 19, 0.12);
  border-radius: 12px;
  background: #FFFFFC;
  padding: 14px;
  color: inherit;
  text-align: left;
  text-decoration: none;
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
}

.scheduler-choice-card:hover {
  border-color: rgba(40, 112, 76, 0.55);
  box-shadow: 0 12px 28px rgba(11, 33, 23, 0.08);
  transform: translateY(-1px);
}

.scheduler-choice-card:focus-visible {
  outline: 2px solid #1F7A3A;
  outline-offset: 2px;
}

.scheduler-choice-icon {
  display: flex;
  height: 42px;
  width: 42px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(40, 112, 76, 0.10);
  color: #1F7A3A;
}

.scheduler-choice-eyebrow {
  display: block;
  color: #28704C;
  font-size: 11px;
  font-weight: 650;
  line-height: 1.2;
  text-transform: uppercase;
}

.scheduler-choice-title {
  display: block;
  margin-top: 5px;
  color: #111827;
  font-size: 16px;
  font-weight: 650;
  line-height: 1.25;
}

.scheduler-choice-description {
  display: block;
  margin-top: 5px;
  color: rgba(17, 24, 39, 0.64);
  font-size: 13px;
  line-height: 1.45;
}

.scheduler-choice-action {
  display: flex;
  height: 34px;
  width: 34px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(20, 20, 19, 0.12);
  border-radius: 999px;
  color: #28704C;
  transition: background-color 160ms ease, border-color 160ms ease, color 160ms ease;
}

.scheduler-choice-card:hover .scheduler-choice-action {
  border-color: #1F7A3A;
  background: #1F7A3A;
  color: #FFFFFC;
}

@media (max-width: 767px) {
  .scheduler-panel {
    padding: 14px;
  }

  .scheduler-panel-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .scheduler-link-pill {
    align-self: flex-start;
  }

  .scheduler-choice-grid {
    grid-template-columns: 1fr;
  }

  .scheduler-choice-card {
    min-height: 112px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .scheduler-choice-card,
  .scheduler-choice-action {
    transition: none;
  }

  .scheduler-choice-card:hover {
    transform: none;
  }
}
</style>
